import { v4 as uuidv4 } from "uuid";
import { NETWORK_ERROR_STATUS } from "./apiError";
import { handleError } from "./../utils/handleError";
import getUrlName from "./getUrlName";
import generateCurl from "./generateCurl";
import updateAppVersion from "./appVersionServices";
import { getSessionToken } from "./sessionToken";
import { isClientEnvironment } from "./clientEnvironment";
import { announceRefusal } from "./staffLinkEntry";
import {
  parseStaffIngressRefusal,
  requestStaffIngressGrace,
} from "./staffIngress";

/** What `fpFetch` hands back when `fetch` itself rejected.
 *
 * Deliberately NOT `Response.error()`, which looks like the obvious answer: it
 * is a real Response, so its `json()` / `text()` / `blob()` resolve to an empty
 * body instead of throwing. Dozens of call sites in this app read a body
 * without first checking `ok`, and for the download and render paths among them
 * — the file tree's download, `RMS/.../DocumentCard`,
 * `downloadFileViewerDataFile`, `RMS/.../InteractionPage`, and both Excel
 * viewers — that throw is their only error signal. Handing them an empty body
 * turns a visible "failed to download" into a silently saved 0-byte file, and
 * an unreadable spreadsheet into a blank grid. Losing loudly beats succeeding
 * wrongly, so the stand-in stays a plain object whose body methods do not exist.
 *
 * (Routing those through `services/downloadApiFile`, which checks `ok`
 * properly, is the real fix and would make `Response.error()` safe. That is
 * its own change.)
 *
 * `statusText` is set because around forty call sites interpolate it straight
 * into user-facing text, where "Network error" says something and `undefined`
 * did not (#11634). Being a plain object also keeps `clone` undefined, so
 * `handleError` below receives this object itself and records that label. A
 * `Response.error()` would not: `clone()` rebuilds from the internal response,
 * dropping a label assigned onto the instance.
 */
function noResponse() {
  return {
    ok: false,
    status: NETWORK_ERROR_STATUS,
    statusText: "Network error",
  };
}

/** Either a real `Response`, or the `noResponse()` stand-in above — which is
 * NOT a Response and has no body methods. Callers must check `ok` before
 * reading a body.
 *
 * @returns {Promise<Response>}
 */
async function fpFetch(url, options = {}, retries = 0) {
  const sessionId = sessionStorage.getItem("sessionId");
  const userEmail = localStorage.getItem("userEmail");
  const requestID = uuidv4();
  let errorObj = null;
  try {
    // User-Email is deprecated. The proper way to do authentication is to use the Authorization header.
    const defaultHeaders = {
      "Session-ID": sessionId || "",
      "User-Email": userEmail || "",
      "Request-ID": requestID,
    };
    let headers = {
      ...defaultHeaders,
      ...options.headers,
    };
    const stytchSession = getSessionToken();
    if (stytchSession) {
      const authHeaders = {
        Authorization: "Bearer " + stytchSession,
      };
      headers = {
        ...authHeaders,
        ...headers,
      };
    }

    const {
      ["x-finpilot-auth"]: needsAuth,
      ["x-fp-ingress-retry"]: ingressRetry,
      // Statuses the caller expects and reads itself (a "none yet" 404, an
      // optimistic-lock 409, #13380): returned as they are, never reported
      // as a failure.
      expectedStatuses,
      ...optionsPlucked
    } = options;
    const fetchOptions = { ...optionsPlucked, headers };

    if (needsAuth && !stytchSession) {
      throw new Error("Must be logged in to make this request.");
    }

    let response = noResponse();

    try {
      response = await fetch(url, fetchOptions);
    } catch (error) {
      // Aborts (e.g. React Query cancelling a superseded request) are
      // expected. Log a breadcrumb to DevTools but rethrow so React Query
      // can recognise its own signal and discard the result rather than
      // caching `undefined`. The rethrow short-circuits handleError/Sentry.
      if (error?.name === "AbortError") {
        console.warn("[fpFetch] request aborted:", url);
        throw error;
      }
      errorObj = error;
    }

    if (
      response &&
      !response.ok &&
      Array.isArray(expectedStatuses) &&
      expectedStatuses.includes(response.status)
    ) {
      return response;
    }

    if (response && response.ok) {
      const appVersion = response.headers.get("Fp-App-Version");
      // console.log("App version: ", appVersion);
      updateAppVersion(appVersion);
      return response;
    } else {
      // Clone the response before any processing to preserve the body
      let responseClone = null;
      if (response && response.clone) {
        try {
          responseClone = response.clone();
        } catch (e) {
          // Silently handle clone errors
        }
      }
      let responseStatus = response?.status;

      // The staff ingress check (#10718): the server flagged what a staff
      // member is adding to a client org as possibly another client's.
      // Ask; on Proceed the host has recorded a grace on the server, so
      // the same request is simply sent again. A retry that is refused
      // again is returned as it is, never asked about twice.
      if (responseStatus === 428 && responseClone && !ingressRetry) {
        let refusal = null;
        try {
          // A clone of the clone: `handleError` below still needs an
          // unread body when this turns out not to be the refusal.
          refusal = parseStaffIngressRefusal(
            await responseClone.clone().json()
          );
        } catch {
          refusal = null;
        }
        if (refusal !== null) {
          const proceeded = await requestStaffIngressGrace(refusal);
          if (proceeded) {
            return fpFetch(
              url,
              { ...options, ["x-fp-ingress-retry"]: true },
              retries
            );
          }
          return response;
        }
      }

      // In a client tab a 401 means the grant ended, not that the staff
      // member's own session did: their home tabs stay signed in (#10718).
      if (responseStatus === 401 && !isClientEnvironment()) {
        try {
          // Broadcast logout to other tabs and clear minimal client state
          localStorage.setItem("fp_logout", String(Date.now()));
          sessionStorage.removeItem("sessionId");
        } catch (e) {}
      }

      // A staff link into a client (#10718 slice 4): the app shell decides
      // whether this refusal is a page that should open the client.
      if (responseStatus !== undefined) {
        announceRefusal(responseStatus, fetchOptions.method || "GET");
      }

      let retryMessage = "";
      if (responseStatus >= 400 && responseStatus < 502) {
        retryMessage = "No retries for 400+ status. Status: " + responseStatus;
      } else {
        retryMessage =
          retries === 3
            ? "Trying again..."
            : retries === 2
              ? "Trying 3rd time..."
              : retries === 1
                ? "Last try..."
                : "No more retries";
      }

      if (retries === 0 || (responseStatus >= 400 && responseStatus < 502)) {
        handleError({
          message: "API call failed. ",
          urlName: getUrlName(url),
          url,
          error: errorObj || "API call failed.",
          location: "L0096226 fpFetch centralised API call function",
          code: "API_CALL_FAILED",
          userEmail,
          sessionId,
          requestID,
          urlName: getUrlName(url),
          url,
          curl: generateCurl(url, fetchOptions),
          failedResponse: responseClone || response,
          navigatorOnline: navigator.onLine,
        });
      }

      if (retries > 0) {
        return fpFetch(url, options, retries - 1);
      } else {
        // return response;
      }

      return response;
    }
  } catch (error) {
    // An abort already left its breadcrumb above; it is not a failure.
    if (error?.name !== "AbortError") console.error(error);
    throw error;
  }
}

export default fpFetch;
